import {
  Action,
  Disclosure,
  Input,
  Modal,
  OrderLineItem,
  RelativeDateTime,
  SimpleSummary,
  SpinnerIcon,
} from 'corso-admin-ui';
import { useState } from 'react';
import { useProductGroupProducts } from '~/features/settings/hooks/useProductGroups';
import { ProductGroup } from '~/types';

export default function ProductGroupProducts({
  group,
}: {
  group: ProductGroup;
}) {
  const { id: groupId, name: groupName, lastUpdatedOn } = group;
  const { data: products = [], isLoading } = useProductGroupProducts(groupId);

  const [showProducts, setShowProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = products.filter(
    (product) =>
      product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.variants.some((variant) =>
        variant.title.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
  );

  return (
    <>
      <Action
        icon="eye"
        accessibilityLabel={`View products in ${group.name}`}
        onClick={() => setShowProducts(true)}
        variant="ghost"
      />
      <Modal
        title={groupName}
        description="All products that match type, collection and tag criteria (archived, deleted and draft products are not included)."
        show={showProducts}
        onClose={() => setShowProducts(false)}
        size="lg"
      >
        <Input
          id="product-search"
          placeholder="Search products"
          label="Search Products"
          labelVisuallyHidden
          defaultValue={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {isLoading ? (
          <div className="flex h-12 w-full items-center justify-center">
            <SpinnerIcon />
          </div>
        ) : (
          <>
            <div className="text-sm text-neutral-500">
              Total products: {filteredProducts.length}
            </div>
            <ul className="flex max-h-96 flex-col gap-2 overflow-y-scroll">
              {filteredProducts.map((product) => (
                <li key={product.id}>
                  <Disclosure
                    defaultOpen={false}
                    renderSummary={() => (
                      <SimpleSummary>
                        <OrderLineItem
                          title={product.title}
                          src={product.imgUrl}
                        >
                          <OrderLineItem.Detail>
                            {product.variants.length} variant
                            {product.variants.length !== 1 ? 's' : ''}
                          </OrderLineItem.Detail>
                        </OrderLineItem>
                      </SimpleSummary>
                    )}
                  >
                    <ul className="flex flex-col gap-2 p-3">
                      {product.variants.map((variant) => (
                        <li key={variant.id} className="pl-4">
                          <OrderLineItem
                            title={variant.title}
                            src={variant.imgUrl ?? product.imgUrl}
                            sku={variant.sku}
                          />
                        </li>
                      ))}
                    </ul>
                  </Disclosure>
                </li>
              ))}
            </ul>
          </>
        )}
        {lastUpdatedOn && (
          <div className="text-sm text-neutral-500">
            Refreshed <RelativeDateTime dateTime={lastUpdatedOn} />
          </div>
        )}
      </Modal>
    </>
  );
}
